import { hotkeys } from 'react-keyboard-shortcuts';
import { Typography } from '@material-ui/core';
import PagingFilter from '../../core/PagingFilter';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ActionText from '../../core/ActionText';
import AreYouSure from '../../core/AreYouSure';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import Locked from '../../core/Locked';
import Notification from '../../core/Notification';
import ProjectSummary from '../../core/project/ProjectSummary';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import ResourceLink from '../../core/ResourceLink';
import { globalStyles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import { checkResourceEntitlements, getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import AddTemplateDialog from './AddTemplateDialog';
import FindClassDialog from './FindClassDialog';
import FindPropertyDialog from './FindPropertyDialog';
import TemplateCard from './TemplateCard';
import TemplateManager from './TemplateManager';

const styles = () => {
    return Object.assign({
        associatedProject: {
            marginTop: 50,
            padding: 12,
            borderRadius: 4,
            backgroundColor: '#f5f5f5'
        }
    }, globalStyles);
};

class AssociatedProject extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            exists: false,
            loading: true,
            isLoadingTemplates: true,
            templates: [],
            filteredTemplates: [],
            templateFilterText: "",
            displayTemplateIRI: "",
            isLoaded: props.data.associatedTemplate && props.data.associatedTemplate.length > 0 ? false : true,
            isErrored: null,
            areYouSure: false,
            visibleTemplates: props.data.associatedTemplate ? [...props.data.associatedTemplate] : []
        };
        this.templateRef = React.createRef();
        this.cancellable = null;
    }

    hot_keys = {
        'alt+t': {
            priority: 1,
            handler: () => this.scrollToTemplates(),
        }
    };

    componentDidMount() {
        this.collectData().then(() => {
            this.checkIfExists();
        });
    }

    static getDerivedStateFromProps(props, state) {
        if (props.data.associatedTemplate && props.version !== state.version) {
            return {
                isLoadingTemplates: true,
                isLoaded: false,
                version: props.version
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.isLoaded && this.props.data.associatedTemplate) this.collectData();
    }

    async collectData() {
        // We load all the templates
        if (!this.state.isLoaded) {
            if (this.props.data.associatedTemplate.length > 0) {
                try {
                    await this.loadData(this.props.data.associatedTemplate).promise;
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    this.sortTemplates();
                }
                catch (e) {
                    if (!(this.cancellable) || (this.cancellable && !this.cancellable.isCancelled())) {
                        this.setState({ isErrored: e, isLoadingTemplates: false, isLoaded: true });
                    }
                }
            }
            else {
                this.setState({ isLoadingTemplates: false, isLoaded: true });
            }
        }
        else {
            this.setState({ isLoadingTemplates: false });
        }
    }

    sortTemplates() {
        let templates = this.props.data.associatedTemplate.map(id => GS.data(id, this.props.graph));
        let sortedTemplates = sortObjects(templates, 'label', this.props.defaultLang, null, false)
            .map(template => template.id);
        // We need to filter in case incoming 
        const filteredTemplates = [...sortedTemplates].filter(at => {
            if (!GS.isLoaded(at, this.props.graph)) return false;
            if (this.state.templateFilterText === "") return true;
            const text = this.state.templateFilterText.toLowerCase();
            const atData = GS.data(at, this.props.graph);
            if (atData.label && atData.label[this.props.defaultLang]
                && atData.label[this.props.defaultLang].toLowerCase().includes(text)) return true;
            return false;
        });
        this.setState({ isLoadingTemplates: false, isLoaded: true, templates: sortedTemplates, filteredTemplates });
    }

    loadData(iris) {
        this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph));
        return this.cancellable;
    }

    scrollToTemplates = () => {
        if (this.templateRef && this.templateRef.current && !this.props.data.pendingDeletion) {
            this.templateRef.current.scrollIntoView({
                block: 'start',
                inline: 'center',
            });
            this.setState({ displayTemplateIRI: "" });
        }
    };

    buildLink = (resourceData) => {
        const ontology = resourceData.visibility || resourceData.isDefinedBy;
        if (!ontology) return null;
        let view = "class";
        if (resourceData.type.includes("ObjectProperty")) {
            view = "objectProperty";
        }
        else if (resourceData.type.includes("DatatypeProperty")) {
            view = "datatypeProperty";
        }
        else if (resourceData.type.includes("AnnotationProperty")) {
            view = "annotationProperty";
        }

        let link = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' +
            this.props.data.associatedProject + "&ontology=" + ontology + "&view=" + view + "&" + view + "=" + resourceData.id;
        return link;
    };

    onAddTemplate = async (label) => {
        const templateIRI = "https://grafsync.graphifi.com/data/shape/" + uuidv4();
        let newTemplate = {
            id: templateIRI,
            type: "NodeShape",
            label: { [this.props.defaultLang]: label }
        };
        await GS.insertResource(newTemplate, templateIRI, this.props.graph);
        this.props.onAdd("associatedTemplate", templateIRI);
        this.toggleCardView(templateIRI);
    };

    onDeleteTemplate = (iri) => GS.deleteResource(iri, this.props.graph);

    onAddClass = (value) => {
        // Just check somebody else hasn't just added it
        if (!this.props.data.associatedClass || !this.props.data.associatedClass.includes(value)) {
            this.props.onAdd("associatedClass", value);
        }
    };

    onAddProperty = (value) => {
        // Just check somebody else hasn't just added it
        if (!this.props.data.associatedProperty || !this.props.data.associatedProperty.includes(value)) {
            this.props.onAdd("associatedProperty", value);
        }
    };

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    checkIfExists = async () => {
        try {
            const isFound = await this.doFind({
                value: this.props.data.associatedProject,
                graph: getGraphFromProject(this.props.data.associatedProject)
            }).promise;
            let exists = isFound.exists;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ exists, loading: false });
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ loading: false, isErrored: e });
        }
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    checkIfSure = () => this.setState({ areYouSure: true });

    onSure = () => {
        this.setState({ areYouSure: false });
        // Clean up any used templates so they get removed where used
        if (this.props.data.associatedTemplate) {
            for (let at of this.props.data.associatedTemplate) {
                this.onDeleteTemplate(at);
            }
        }
        this.props.onDeleteProject(this.props.id);
    };

    onNotSure = () => this.setState({ areYouSure: false });

    handleTemplateFilterChange = (text) => {
        const filteredTemplates = this.state.templates.filter(at => {
            if (!GS.isLoaded(at, this.props.graph)) return false;
            if (text === "") return true;
            const atData = GS.data(at, this.props.graph);
            if (atData.label && atData.label[this.props.defaultLang]
                && atData.label[this.props.defaultLang].toLowerCase().includes(text.toLowerCase())) return true;
            return false;
        });
        this.setState({ templateFilterText: text.trim(), filteredTemplates });
    };

    toggleCardView = (iri) => {
        let visibleTemplates = [...this.state.visibleTemplates];
        if (visibleTemplates.includes(iri)) {
            visibleTemplates.splice(visibleTemplates.indexOf(iri), 1);
        }
        else {
            visibleTemplates.push(iri);
        }
        this.setState({ visibleTemplates });
    };

    jumpToCard = (iri) => this.setState({ displayTemplateIRI: iri });

    render() {

        const { classes, data, defaultLang, graph, lockedGraphs } = this.props;
        const { exists, loading, isErrored, areYouSure, visibleTemplates, templates, displayTemplateIRI,
            templateFilterText, filteredTemplates } = this.state;

        if (isErrored) return <Errored dataTest="ap-error" data={isErrored} />;
        const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_TAXONOMY_PROJECT);
        const associatedProjectGraph = getGraphFromProject(data.associatedProject);
        const locked = lockedGraphs.some(lg => getGraphFromProject(data.associatedProject) === lg);
        if (locked) return <Locked />;

        return (
            <ProjectConsumer>
                {({ id: projectIRI, skosXL }) => (
                    <div data-test={data.associatedProject} elevation={0} className={classes.associatedProject}>
                        {loading ?
                            <Loading />
                            :
                            <div>
                                <div style={{ display: 'flex', marginBottom: 12 }}>
                                    <div style={{ flex: '1' }}>
                                        {exists ?
                                            <ProjectSummary
                                                id={data.associatedProject}
                                                graph={associatedProjectGraph}
                                                lang={defaultLang}
                                                handleNotAvailable={true} />
                                            :
                                            <div style={{ marginBottom: 12 }}>
                                                <Notification error={false} text="The associated project has been deleted." dataTest="warning" />
                                            </div>
                                        }
                                    </div>
                                    {!isReadOnly &&
                                        <div style={{ flex: '0 0' }} data-test="delete-associated-project-option" >
                                            <Tooltip placement="right" title='Delete this association'>
                                                <IconButton
                                                    data-test="delete-associated-project"
                                                    size="small"
                                                    onClick={this.checkIfSure}
                                                    className={classes.deleteButton}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </div>
                                    }
                                </div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="associated-classes" height="full" title="Classes to Make Available for Taxonomies" actions=
                                            {!isReadOnly ?
                                                <FindClassDialog
                                                    defaultLang={defaultLang}
                                                    projects={[data.associatedProject]}
                                                    exclude={data.associatedClass}
                                                    onAddClass={this.onAddClass} />
                                                :
                                                null
                                            }>
                                            {data.associatedClass &&
                                                [...data.associatedClass].sort().map((ac, index) =>
                                                    <ResourceLink
                                                        dataTest={"class-resource-link-" + index}
                                                        key={ac}
                                                        checkIfUsedOnDelete={
                                                            {
                                                                graph,
                                                                value: ac,
                                                                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                                                                message: [
                                                                    "This associated class is used within this project.",
                                                                    "Removing it will not remove data using it but will alter the class link to be displayed as an IRI.",
                                                                    "Do you want to proceed with removing this class?"
                                                                ]
                                                            }
                                                        }
                                                        id={ac}
                                                        link={this.buildLink}
                                                        handleNotAvailable={true}
                                                        isReadOnly={isReadOnly}
                                                        projectGraph={graph}
                                                        graph={associatedProjectGraph}
                                                        property="associatedClass"
                                                        source={data.id}
                                                        viewLang={defaultLang}
                                                        defaultLang={defaultLang} />
                                                )}
                                        </TitledPanel>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TitledPanel dataTest="associated-properties" height="full" title="Global Properties to Make Available for Taxonomies" actions=
                                            {!isReadOnly && exists ?
                                                <FindPropertyDialog
                                                    defaultLang={defaultLang}
                                                    projects={[data.associatedProject]}
                                                    exclude={data.associatedProperty}
                                                    onAddProperty={this.onAddProperty} />
                                                :
                                                null
                                            }>
                                            {data.associatedProperty &&
                                                [...data.associatedProperty].sort().map((ap, index) =>
                                                    <ResourceLink
                                                        dataTest={"property-resource-link-" + index}
                                                        key={ap}
                                                        id={ap}
                                                        link={this.buildLink}
                                                        checkIfUsedOnDelete={
                                                            {
                                                                graph,
                                                                predicate: GS.getIRIfromAlias(ap) || ap,
                                                                message: [
                                                                    "This associated property is used within this project.",
                                                                    "Removing it will not remove data using it but the data may only be visible in the Triples Viewer.",
                                                                    "Do you want to proceed with removing this property?"
                                                                ]
                                                            }
                                                        }
                                                        handleNotAvailable={true}
                                                        isReadOnly={isReadOnly}
                                                        projectGraph={graph}
                                                        graph={associatedProjectGraph}
                                                        property="associatedProperty"
                                                        source={data.id}
                                                        viewLang={defaultLang}
                                                        defaultLang={defaultLang} />
                                                )}
                                        </TitledPanel>
                                    </Grid>
                                    <Grid item xs={12} ref={this.templateRef}>
                                        <TitledPanel dataTest="templates" height="full" title="Templates" actions=
                                            {!isReadOnly ?
                                                <AddTemplateDialog defaultLang={defaultLang} onAddTemplate={this.onAddTemplate} />
                                                :
                                                null
                                            }>
                                            <div style={{ width: '100%' }}>
                                                {templates && templates.length > 1 &&
                                                    <div>
                                                        <div style={{ maxWidth: '240px', marginBottom: 12 }}>
                                                            <Typography data-test="showing" variant="body2" className={classes.showing}>Showing {filteredTemplates.length} of {templates.length}.</Typography>
                                                            <PagingFilter
                                                                includeLabel={false}
                                                                handleFilterChange={this.handleTemplateFilterChange}
                                                                placeholder="Filter templates"
                                                                text={templateFilterText}
                                                                maxLength={50} />
                                                        </div>
                                                        {displayTemplateIRI !== "" &&
                                                            <div style={{ position: 'fixed', right: 10, bottom: 10, backgroundColor: '#13294B', border: 'solid 4px #13294B', borderRadius: 4 }}>
                                                                <Tooltip placement="right" title='Jump to template filter (Alt+t)'>
                                                                    <IconButton
                                                                        data-test="jump-to-filter"
                                                                        size="small"
                                                                        style={{ color: 'white' }}
                                                                        onClick={this.scrollToTemplates}>
                                                                        <ArrowUpwardIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </div>
                                                        }
                                                    </div>
                                                }
                                                {templates && templates.length > 0 ?
                                                    <div style={{ width: '100%' }}>
                                                        <div data-test="card-list" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', width: '100%' }}>
                                                            {templates
                                                                .filter(at => filteredTemplates.includes(at))
                                                                .map((at) =>
                                                                    <TemplateCard
                                                                        key={at}
                                                                        graph={graph}
                                                                        jumpToCard={this.jumpToCard}
                                                                        onToggleView={this.toggleCardView}
                                                                        visible={visibleTemplates.includes(at)}
                                                                        defaultLang={defaultLang}
                                                                        id={at} />
                                                                )}
                                                        </div>
                                                        <div data-test="template-list" style={{ marginTop: 24 }}>
                                                            {visibleTemplates.length === 0 ?
                                                                <ActionText offset="30px">Select templates to view.</ActionText>
                                                                :
                                                                templates
                                                                    .filter(at => visibleTemplates.includes(at) && filteredTemplates.includes(at))
                                                                    .map((at, index) =>
                                                                        <div
                                                                            data-test={"template-" + index}
                                                                            key={at}
                                                                            style={{
                                                                                backgroundColor: 'white',
                                                                                padding: 12,
                                                                                marginBottom: 36,
                                                                                borderRadius: 4
                                                                            }}>
                                                                            <TemplateManager
                                                                                key={at}
                                                                                skosXL={skosXL}
                                                                                graph={graph}
                                                                                displayTemplateIRI={displayTemplateIRI}
                                                                                project={projectIRI}
                                                                                buildLink={this.buildLink}
                                                                                isReadOnly={isReadOnly}
                                                                                onDeleteTemplate={this.onDeleteTemplate}
                                                                                allowedClasses={data.associatedClass}
                                                                                ontologyProject={data.associatedProject}
                                                                                ontologyProjectGraph={associatedProjectGraph}
                                                                                defaultLang={defaultLang}
                                                                                id={at} />
                                                                        </div>
                                                                    )
                                                            }
                                                        </div>
                                                    </div>
                                                    :
                                                    <ActionText offset="30px">No templates available.</ActionText>
                                                }
                                            </div>
                                        </TitledPanel>
                                    </Grid>
                                </Grid>
                            </div>
                        }
                        {areYouSure && <AreYouSure
                            onSure={this.onSure}
                            onNotSure={this.onNotSure}
                            message={[
                                "Do you really want to delete this association?",
                                "If there are classes that have been used the data will not be removed but the classes will revert to being displayed as IRIs.",
                                "If there are properties that have been used the data will not be removed but will only be visible in the Triple Viewer."
                            ]} />}
                    </div>
                )
                }
            </ProjectConsumer>
        );
    }
}

AssociatedProject.propTypes = {
    classes: PropTypes.object,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    onDeleteProject: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    lockedGraphs: PropTypes.array,
    data: PropTypes.object.isRequired
};

export default withStyles(styles)(withGS(hotkeys(AssociatedProject)));